import { Popover, RouteIcon, type Point, type Spot, type WayEnd } from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SpotField } from './SpotField';

interface Props {
  /** Where it leaves from and arrives at now. */
  from: WayEnd;
  to: WayEnd;
  /** The two stops, to look for a place near each. */
  near: { from: Point | null; to: Point | null };
  /** Keeps the ends: a place said, or `null` to work it out again. */
  onSave: (ends: { from: Spot | null; to: Spot | null }) => void;
}

/** One end of a way: an airport near, a place found by its name or address, or worked out. */
function EndChoice({
  label,
  end,
  near,
  onPick,
}: {
  label: string;
  end: WayEnd;
  near: Point | null;
  onPick: (spot: Spot | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="ends-side">
      <legend className="field-label">{label}</legend>
      {end.airports.length > 0 && (
        <div className="ends-airports">
          {end.airports.map((airport) => (
            <button
              key={airport.name}
              type="button"
              className="way-pick-option"
              aria-pressed={airport.name === end.name}
              onClick={() => {
                onPick(airport);
              }}
            >
              <span aria-hidden="true">✈</span>
              <span>{airport.name}</span>
            </button>
          ))}
        </div>
      )}
      <SpotField
        label={t('plan.endPlace')}
        near={near}
        spot={
          end.own && !end.airports.some((a) => a.name === end.name)
            ? { ...end, detail: '' }
            : null
        }
        onPick={onPick}
        onClear={() => {
          onPick(null);
        }}
      />
    </fieldset>
  );
}

/** Where a way leaves from and arrives at: the airport of a flight, the station, an address. */
export function EndsPick({ from, to, near, onSave }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const kept = (end: WayEnd): Spot | null => (end.own ? { ...end, detail: '' } : null);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={t('plan.ends')}
      trigger={(props) => (
        <button
          {...props}
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('plan.ends')}
          title={t('plan.ends')}
        >
          <RouteIcon />
        </button>
      )}
    >
      <div className="ends-pick">
        <EndChoice
          label={t('plan.endFrom')}
          end={from}
          near={near.from}
          onPick={(spot) => {
            setOpen(false);
            onSave({ from: spot, to: kept(to) });
          }}
        />
        <EndChoice
          label={t('plan.endTo')}
          end={to}
          near={near.to}
          onPick={(spot) => {
            setOpen(false);
            onSave({ from: kept(from), to: spot });
          }}
        />
      </div>
    </Popover>
  );
}
